import { Injectable } from '@angular/core';
import { LandingPageImportFrontmatter, LandingPageImportPreview, LandingPageImportWarning } from '../models/landing-page-import-models';
import { LandingPageBlock, LandingPageBlockType, LandingPageInput, LandingPagePublicationState, normalizePath, normalizeTags, pathify } from '../models/cms-models';

@Injectable({
  providedIn: 'root',
})
export class LandingPageMarkdownImportService {
  parse(siteId: string, markdown: string, sourceName: string | null = null, now = new Date()): LandingPageImportPreview {
    const warnings: LandingPageImportWarning[] = [];
    const normalizedMarkdown = this.normalizeMarkdown(markdown);
    const { rawFrontmatter, body } = this.extractFrontmatter(normalizedMarkdown);
    const frontmatter = this.parseFrontmatter(rawFrontmatter, warnings);
    const titleResolution = this.resolveTitle(frontmatter.title, body, sourceName, warnings);
    const bodyWithoutMarkers = titleResolution.body.replaceAll('<!--more-->', '');

    if (this.rawHtmlPattern.test(bodyWithoutMarkers)) {
      this.addWarning(warnings, 'raw-html', 'Raw HTML was preserved inside text blocks for manual review.');
    }

    const blocks = this.parseBlocks(bodyWithoutMarkers, warnings);
    const summaryResolution = this.resolveSummary(frontmatter, titleResolution.body, blocks);
    const publicationResolution = this.resolvePublication(frontmatter.date, now, warnings);

    const landingPage: LandingPageInput = {
      siteId,
      title: titleResolution.title,
      path: this.resolvePath(frontmatter, titleResolution.title),
      metadata: {
        summary: summaryResolution.summary,
        metaTitle: this.cleanOptionalText(frontmatter.metaTitle),
        metaDescription: this.cleanOptionalText(frontmatter.metaDescription),
        metaImageUrl: this.cleanOptionalText(frontmatter.metaImageUrl),
      },
      tags: normalizeTags(frontmatter.tags),
      publicationState: publicationResolution.state,
      scheduledPublishAt: publicationResolution.state === 'Scheduled' ? publicationResolution.isoValue : null,
      publishedAt: publicationResolution.state === 'Published' ? publicationResolution.isoValue : null,
      blocks,
    };

    return {
      landingPage,
      sourceName,
      titleSource: titleResolution.source,
      summarySource: summaryResolution.source,
      detectedDate: publicationResolution.isoValue,
      detectedPublicationState: publicationResolution.state,
      frontmatter,
      warnings,
      blockCounts: this.buildBlockCounts(blocks),
    };
  }

  private readonly rawHtmlPattern = /<(?!!--)[a-z/][^>]*>/i;

  private normalizeMarkdown(markdown: string): string {
    return markdown.replace(/^\ufeff/, '').replace(/\r\n?/g, '\n');
  }

  private extractFrontmatter(markdown: string): { rawFrontmatter: string | null; body: string } {
    const match = markdown.match(/^---\n([\s\S]*?)\n---(?:\n|$)/);
    if (!match) {
      return {
        rawFrontmatter: null,
        body: markdown.trim(),
      };
    }

    return {
      rawFrontmatter: match[1],
      body: markdown.slice(match[0].length).trim(),
    };
  }

  private parseFrontmatter(rawFrontmatter: string | null, warnings: LandingPageImportWarning[]): LandingPageImportFrontmatter {
    const frontmatter: LandingPageImportFrontmatter = {
      title: null,
      path: null,
      slug: null,
      tags: [],
      date: null,
      summary: null,
      description: null,
      metaTitle: null,
      metaDescription: null,
      metaImageUrl: null,
    };

    if (!rawFrontmatter) {
      return frontmatter;
    }

    for (const rawLine of rawFrontmatter.split('\n')) {
      const line = rawLine.trim();
      if (!line) {
        continue;
      }

      const match = line.match(/^([A-Za-z][A-Za-z0-9_-]*):\s*(.*)$/);
      if (!match) {
        this.addWarning(warnings, 'frontmatter', `Unsupported frontmatter line: ${line}`);
        continue;
      }

      const [, rawKey, rawValue] = match;
      const key = rawKey.trim();
      const value = rawValue.trim();

      switch (key) {
        case 'title':
          frontmatter.title = this.cleanOptionalText(this.parseScalarValue(value));
          break;
        case 'path':
          frontmatter.path = this.cleanOptionalText(this.parseScalarValue(value));
          break;
        case 'slug':
          frontmatter.slug = this.cleanOptionalText(this.parseScalarValue(value));
          break;
        case 'date':
          frontmatter.date = this.cleanOptionalText(this.parseScalarValue(value));
          break;
        case 'summary':
          frontmatter.summary = this.cleanOptionalText(this.parseScalarValue(value));
          break;
        case 'description':
          frontmatter.description = this.cleanOptionalText(this.parseScalarValue(value));
          break;
        case 'metaTitle':
          frontmatter.metaTitle = this.cleanOptionalText(this.parseScalarValue(value));
          break;
        case 'metaDescription':
          frontmatter.metaDescription = this.cleanOptionalText(this.parseScalarValue(value));
          break;
        case 'metaImageUrl':
          frontmatter.metaImageUrl = this.cleanOptionalText(this.parseScalarValue(value));
          break;
        case 'tags':
          frontmatter.tags = this.parseTagList(value, warnings);
          break;
        default:
          this.addWarning(warnings, 'frontmatter', `Unsupported frontmatter key "${key}" was ignored.`);
          break;
      }
    }

    return frontmatter;
  }

  private parseScalarValue(value: string): string {
    if (!value) {
      return '';
    }

    const trimmed = value.trim();
    if (
      (trimmed.startsWith('"') && trimmed.endsWith('"'))
      || (trimmed.startsWith("'") && trimmed.endsWith("'"))
    ) {
      return trimmed.slice(1, -1);
    }

    return trimmed;
  }

  private resolvePath(frontmatter: LandingPageImportFrontmatter, title: string): string {
    if (frontmatter.path) {
      return normalizePath(frontmatter.path);
    }

    if (frontmatter.slug) {
      return normalizePath(frontmatter.slug);
    }

    return pathify(title);
  }

  private parseTagList(value: string, warnings: LandingPageImportWarning[]): string[] {
    if (!value) {
      return [];
    }

    const trimmed = value.trim();
    if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) {
      return normalizeTags([this.parseScalarValue(trimmed)]);
    }

    const body = trimmed.slice(1, -1);
    const items: string[] = [];
    let current = '';
    let quote: '"' | "'" | null = null;

    for (const character of body) {
      if ((character === '"' || character === "'") && (!quote || quote === character)) {
        quote = quote ? null : character;
        current += character;
        continue;
      }

      if (character === ',' && !quote) {
        const parsed = this.cleanOptionalText(this.parseScalarValue(current));
        if (parsed) {
          items.push(parsed);
        }
        current = '';
        continue;
      }

      current += character;
    }

    if (quote) {
      this.addWarning(warnings, 'frontmatter', 'Malformed tag list in frontmatter. Tags may be incomplete.');
    }

    const parsed = this.cleanOptionalText(this.parseScalarValue(current));
    if (parsed) {
      items.push(parsed);
    }

    return normalizeTags(items);
  }

  private resolveTitle(
    explicitTitle: string | null,
    body: string,
    sourceName: string | null,
    warnings: LandingPageImportWarning[],
  ): { title: string; source: LandingPageImportPreview['titleSource']; body: string } {
    if (explicitTitle) {
      return {
        title: explicitTitle,
        source: 'frontmatter',
        body,
      };
    }

    const bodyLines = body.split('\n');
    for (let index = 0; index < bodyLines.length; index += 1) {
      const match = bodyLines[index].match(/^#\s+(.+?)\s*$/);
      if (!match) {
        continue;
      }

      const nextLines = [...bodyLines];
      nextLines.splice(index, 1);
      if (nextLines[index]?.trim() === '') {
        nextLines.splice(index, 1);
      }

      this.addWarning(warnings, 'missing-title', 'Title was missing in frontmatter. The first H1 heading was used instead.');
      return {
        title: match[1].trim(),
        source: 'heading',
        body: nextLines.join('\n').trim(),
      };
    }

    if (sourceName) {
      this.addWarning(warnings, 'missing-title', 'Title was missing in frontmatter. The file name was used instead.');
      return {
        title: this.humanizeFileName(sourceName),
        source: 'filename',
        body,
      };
    }

    this.addWarning(warnings, 'missing-title', 'Title was missing in frontmatter. A default title was used.');
    return {
      title: 'Untitled landingPage',
      source: 'default',
      body,
    };
  }

  private resolveSummary(
    frontmatter: LandingPageImportFrontmatter,
    body: string,
    blocks: LandingPageBlock[],
  ): { summary: string; source: LandingPageImportPreview['summarySource'] } {
    const explicitSummary = this.cleanOptionalText(frontmatter.summary);
    if (explicitSummary) {
      return {
        summary: explicitSummary,
        source: 'frontmatter-summary',
      };
    }

    const descriptionSummary = this.cleanOptionalText(frontmatter.description);
    if (descriptionSummary) {
      return {
        summary: descriptionSummary,
        source: 'frontmatter-description',
      };
    }

    const excerpt = body.includes('<!--more-->')
      ? this.cleanOptionalText(this.toPlainText(body.split('<!--more-->')[0]))
      : null;
    if (excerpt) {
      return {
        summary: excerpt,
        source: 'excerpt',
      };
    }

    const firstTextBlock = blocks.find((block) => block.type === 'text' && this.cleanOptionalText(block.textContent ?? ''));
    if (firstTextBlock?.textContent) {
      return {
        summary: this.toPlainText(firstTextBlock.textContent),
        source: 'first-text-block',
      };
    }

    return {
      summary: 'Imported from Markdown.',
      source: 'default',
    };
  }

  private resolvePublication(
    rawDate: string | null,
    now: Date,
    warnings: LandingPageImportWarning[],
  ): { isoValue: string | null; state: LandingPagePublicationState } {
    if (!rawDate) {
      this.addWarning(warnings, 'missing-date', 'Date was missing in frontmatter. The landingPage will import as a draft.');
      return {
        isoValue: null,
        state: 'Draft',
      };
    }

    const parsedDate = this.parseDateValue(rawDate);
    if (!parsedDate) {
      this.addWarning(warnings, 'invalid-date', `The frontmatter date "${rawDate}" could not be parsed. The landingPage will import as a draft.`);
      return {
        isoValue: null,
        state: 'Draft',
      };
    }

    return {
      isoValue: parsedDate.toISOString(),
      state: parsedDate.getTime() > now.getTime() ? 'Scheduled' : 'Published',
    };
  }

  private parseDateValue(rawDate: string): Date | null {
    const trimmed = rawDate.trim();
    if (!trimmed) {
      return null;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [year, month, day] = trimmed.split('-').map((value) => Number.parseInt(value, 10));
      return new Date(year, month - 1, day, 0, 0, 0, 0);
    }

    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return parsed;
  }

  private parseBlocks(body: string, warnings: LandingPageImportWarning[]): LandingPageBlock[] {
    const blocks: LandingPageBlock[] = [];
    const textBuffer: string[] = [];
    const lines = body.split('\n');
    let lineIndex = 0;

    const pushTextBlock = (): void => {
      const content = this.trimTextBuffer(textBuffer);
      textBuffer.length = 0;

      if (!content) {
        return;
      }

      blocks.push(this.createEmptyBlock(blocks.length, 'text', { textContent: content }));
    };

    while (lineIndex < lines.length) {
      const line = lines[lineIndex];
      const trimmedLine = line.trim();
      const codeFenceMatch = trimmedLine.match(/^```([^`\s]+)?\s*$/);
      const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.+?)\s*$/);
      const imageMatch = trimmedLine.match(/^!\[([^\]]*)\]\(([^)\s]+)(?:\s+"([^"]*)")?\)\s*$/);

      if (codeFenceMatch) {
        pushTextBlock();

        const codeLines: string[] = [];
        const language = codeFenceMatch[1]?.trim().toLowerCase() || 'text';
        let closingFenceFound = false;

        lineIndex += 1;
        while (lineIndex < lines.length) {
          if (lines[lineIndex].trim().match(/^```+\s*$/)) {
            closingFenceFound = true;
            break;
          }

          codeLines.push(lines[lineIndex]);
          lineIndex += 1;
        }

        if (!codeFenceMatch[1]?.trim()) {
          this.addWarning(warnings, 'missing-code-language', 'A fenced code block had no language. It was imported as plain text code.');
        }

        if (!closingFenceFound) {
          this.addWarning(warnings, 'unterminated-code-fence', 'A fenced code block was missing its closing fence. The remaining content was imported as code.');
        }

        blocks.push(this.createEmptyBlock(blocks.length, 'codeblock', {
          codeLanguage: language,
          codeContent: codeLines.join('\n').trimEnd(),
        }));

        lineIndex += 1;
        continue;
      }

      if (headingMatch) {
        pushTextBlock();
        blocks.push(this.createEmptyBlock(blocks.length, 'heading', {
          heading: headingMatch[2].replace(/\s+#+\s*$/, '').trim(),
          headingLevel: headingMatch[1].length,
        }));
        lineIndex += 1;
        continue;
      }

      if (imageMatch) {
        pushTextBlock();

        if (!this.isAbsoluteUrl(imageMatch[2])) {
          this.addWarning(warnings, 'relative-image-url', `Image URL "${imageMatch[2]}" is relative and may need manual adjustment.`);
        }

        this.addWarning(
          warnings,
          'external-image-block',
          `Image "${imageMatch[2]}" was preserved as text because reusable image records must be selected manually after import.`,
        );
        blocks.push(this.createEmptyBlock(blocks.length, 'text', {
          textContent: line,
        }));
        lineIndex += 1;
        continue;
      }

      textBuffer.push(line);
      lineIndex += 1;
    }

    pushTextBlock();
    return blocks;
  }

  private trimTextBuffer(lines: string[]): string {
    const nextLines = [...lines];
    while (nextLines.length && !nextLines[0].trim()) {
      nextLines.shift();
    }

    while (nextLines.length && !nextLines[nextLines.length - 1].trim()) {
      nextLines.pop();
    }

    return nextLines.join('\n').trim();
  }

  private createEmptyBlock(sortOrder: number, type: LandingPageBlockType, patch: Partial<LandingPageBlock>): LandingPageBlock {
    return {
      sortOrder,
      type,
      heading: null,
      headingLevel: null,
      textContent: null,
      imageId: null,
      imageAltOverride: null,
      imageCaptionOverride: null,
      youTubeUrl: null,
      codeLanguage: null,
      codeContent: null,
      galleryId: null,
      referenceLinks: [],
      ...patch,
    };
  }

  private buildBlockCounts(blocks: LandingPageBlock[]): LandingPageImportPreview['blockCounts'] {
    const counts = new Map<LandingPageBlockType, number>();

    for (const block of blocks) {
      counts.set(block.type, (counts.get(block.type) ?? 0) + 1);
    }

    return (['heading', 'text', 'image', 'gallery', 'youtubevideo', 'codeblock', 'references'] as LandingPageBlockType[])
      .filter((type) => (counts.get(type) ?? 0) > 0)
      .map((type) => ({
        type,
        count: counts.get(type) ?? 0,
      }));
  }

  private isAbsoluteUrl(value: string): boolean {
    return /^https?:\/\//i.test(value);
  }

  private humanizeFileName(sourceName: string): string {
    return sourceName
      .replace(/\.[^.]+$/, '')
      .replace(/[-_]+/g, ' ')
      .trim()
      .replace(/\b\w/g, (character) => character.toUpperCase()) || 'Untitled landingPage';
  }

  private toPlainText(markdown: string): string {
    return markdown
      .replace(/<!--[\s\S]*?-->/g, ' ')
      .replace(/```[\s\S]*?```/g, ' ')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/^>\s?/gm, '')
      .replace(/^[-*+]\s+/gm, '')
      .replace(/^\d+\.\s+/gm, '')
      .replace(/!\[([^\]]*)\]\(([^)]*)\)/g, '$1')
      .replace(/\[([^\]]+)\]\(([^)]*)\)/g, '$1')
      .replace(/`([^`]*)`/g, '$1')
      .replace(/[*_~]/g, '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private cleanOptionalText(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }

  private addWarning(warnings: LandingPageImportWarning[], code: LandingPageImportWarning['code'], message: string): void {
    if (warnings.some((warning) => warning.code === code && warning.message === message)) {
      return;
    }

    warnings.push({ code, message });
  }
}
